import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { tr } from "@/lib/i18n";
import { useAuth } from "@/context/AuthContext";
import { CRM_INTERACTION_KINDS, CRM_FOLLOWUP_PRESETS } from "@/lib/crm";
import api from "@/lib/api";
import { toast } from "sonner";
import { Mail, Phone, MessageSquare, Handshake, Clapperboard, Paperclip, Loader2, Check } from "lucide-react";

const ICONS = { Mail, Phone, MessageSquare, Handshake, Clapperboard, Paperclip };

export default function ConfirmContactDialog({ open, onOpenChange, contact, onConfirmed }) {
  const { lang } = useAuth();
  const t = (k) => tr(k, lang);
  const [kind, setKind] = useState(null);
  const [note, setNote] = useState("");
  const [followupMode, setFollowupMode] = useState("90");
  const [followupAt, setFollowupAt] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      const current = contact?.followup_days;
      const preset = CRM_FOLLOWUP_PRESETS.includes(current) ? String(current) : "90";
      setKind(null); setNote("");
      setFollowupMode(preset); setFollowupAt("");
    }
  }, [open, contact]);

  const submit = async () => {
    if (!kind) return;
    const payload = { kind, note };
    if (followupMode === "custom") {
      payload.followup_at = followupAt || null;
      payload.followup_days = 0;
    } else {
      payload.followup_days = parseInt(followupMode, 10) || 90;
    }
    setSaving(true);
    try {
      const r = await api.post(`/crm/contacts/${contact.contact_id}/confirm`, payload);
      toast.success(t("crm.confirm.ok"));
      onOpenChange?.(false);
      onConfirmed?.(r.data);
    } catch {
      toast.error(t("crm.confirm.fail"));
    } finally {
      setSaving(false);
    }
  };

  if (!contact) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="crm-confirm-dialog"
        className="max-w-lg bg-stone-950 border-stone-800"
      >
        <DialogHeader>
          <DialogTitle className="serif text-2xl text-stone-100">
            {t("crm.confirm.title")}
          </DialogTitle>
          <DialogDescription className="text-stone-400 text-sm">
            {t("crm.confirm.subtitle")}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          <div className="grid grid-cols-3 gap-2">
            {CRM_INTERACTION_KINDS.map(({ key, icon }) => {
              const Icon = ICONS[icon];
              const active = kind === key;
              return (
                <button
                  key={key}
                  data-testid={`crm-confirm-kind-${key}`}
                  onClick={() => setKind(key)}
                  className={`flex flex-col items-center gap-1 rounded-sm border py-3 px-2 text-xs transition-colors ${
                    active
                      ? "border-amber-500/70 bg-amber-500/10 text-amber-300"
                      : "border-stone-800 text-stone-400 hover:border-stone-600 hover:text-stone-200"
                  }`}
                >
                  <Icon size={18} />
                  <span>{t(`crm.confirm.kind.${key}`)}</span>
                </button>
              );
            })}
          </div>

          <div>
            <label className="label-cap">{t("crm.confirm.note")}</label>
            <textarea
              data-testid="crm-confirm-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder={t("crm.confirm.note_ph")}
              rows={3}
              className="script-input w-full resize-none"
            />
          </div>

          <div>
            <label className="label-cap">{t("crm.confirm.new_followup")}</label>
            <select
              data-testid="crm-confirm-followup-mode"
              value={followupMode}
              onChange={(e) => setFollowupMode(e.target.value)}
              className="script-input w-full bg-stone-950"
            >
              <option value="30">{t("crm.form.days30")}</option>
              <option value="60">{t("crm.form.days60")}</option>
              <option value="90">{t("crm.form.days90")}</option>
              <option value="120">{t("crm.form.days120")}</option>
              <option value="180">{t("crm.form.days180")}</option>
              <option value="custom">{t("crm.form.custom_date")}</option>
            </select>
          </div>

          {followupMode === "custom" && (
            <input
              data-testid="crm-confirm-custom-date"
              type="date"
              value={followupAt}
              onChange={(e) => setFollowupAt(e.target.value)}
              className="script-input w-full bg-stone-950"
            />
          )}

          <div className="flex items-center justify-end gap-2 pt-2">
            <button onClick={() => onOpenChange?.(false)} className="ghost-btn">
              {t("action.cancel")}
            </button>
            <button
              data-testid="crm-confirm-submit"
              onClick={submit}
              disabled={!kind || saving}
              className="pill-btn"
            >
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
              {t("crm.confirm.yes")}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
